import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import classnames from 'classnames'
import { Link } from 'react-router-dom'

import useDeferredCaptchaSubmit from 'hooks/useDeferredCaptchaSubmit'
import { dispatchLogin } from 'reduxModules/auth/dispatchers'
import { loginBoxSelector } from 'reduxModules/app/selectors'
import { CaptchaNotice } from '../CaptchaNotice'
import keyUpHandler from '../../utils/keyUpHandler'
import FacebookSignIn from '../../generic/FacebookSignIn/index'
import OpenAthensSignIn from '../../generic/OpenAthensSignIn/index'
import CaptchaStandalone from '../../CaptchaStandalone/_lazy'
import '../RenderedField/styles.css'
import './styles.css'

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i

export function LoginBox({
  autoFocus = false,
  inCheckout = false,
  isMain = false,
  toggleAuth,
  location,
  recaptcha,
  onSubmit,
  next
}) {
  const [passwordVisible, setPasswordVisible] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty }
  } = useForm()

  // Gate submission on Turnstile: capture an early click and replay it once the
  // token lands, and keep the form locked until the submission settles.
  const { submit, awaitingVerification, isSubmitting } = useDeferredCaptchaSubmit(
    recaptcha.token,
    onSubmit
  )

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible)
  }

  const isSubmitDisabled = !isDirty || isSubmitting || awaitingVerification

  return (
    <div id="LoginBox" role={isMain ? 'main' : 'region'}>
      <div className="sign-in-box auth-form-box">
        {inCheckout && (
          <button
            className="toggle-box"
            onClick={() => toggleAuth(true)}
          >
            Create your account
          </button>
        )}
        <form id="SignInForm" onSubmit={handleSubmit(submit)}>
          <div className={classnames('field', { '-error': errors.email })}>
            <div className="field-container">
              <input
                {...register('email', {
                  required: 'This field is required.',
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: 'Invalid email address.'
                  }
                })}
                type="email"
                placeholder="Email"
                aria-label="Email"
                autoFocus={autoFocus}
              />
              <span
                className={`field__error${!errors.email ? ' field__error--hidden' : ''}`}
                role="alert"
              >
                {errors.email?.message}
              </span>
            </div>
          </div>
          <div className={classnames('field', { '-error': errors.password })}>
            <div className="field-container">
              <input
                {...register('password', {
                  required: 'This field is required.'
                })}
                type={passwordVisible ? 'text' : 'password'}
                placeholder="Password"
                aria-label="Password"
              />
              <i
                className={classnames('icon', passwordVisible ? 'icon-passcodewatch' : 'icon-passcodewatch disabled')}
                aria-label="Toggle password visibility"
                onClick={togglePasswordVisibility}
                onKeyUp={event => keyUpHandler(event, togglePasswordVisibility)}
                tabIndex="0"
              />
              <span
                className={`field__error${!errors.password ? ' field__error--hidden' : ''}`}
                role="alert"
              >
                {errors.password?.message}
              </span>
            </div>
          </div>
          <CaptchaStandalone />
          <button
            type="submit"
            className="btn -wide -blue-2"
            disabled={isSubmitDisabled}
            style={{ width: '100%' }}
          >
            {awaitingVerification ? 'Verifying…' : 'Login'}
          </button>
        </form>

        <div className="divisor">or</div>
        <FacebookSignIn
          text="Facebook Login"
          location={location}
          inCheckout={inCheckout}
        />
        <OpenAthensSignIn />
        {!inCheckout && (
          <div className="auth-alt">
            or <Link to="/users/signup">Register</Link> <br /> to create an account
          </div>
        )}

        <div className="forgot-password">
          <Link to="/users/password/new">Forgot password?</Link>
        </div>
        <CaptchaNotice dark />
      </div>
    </div>
  )
}

LoginBox.propTypes = {
  autoFocus: PropTypes.bool,
  inCheckout: PropTypes.bool,
  isMain: PropTypes.bool,
  toggleAuth: PropTypes.func,
  location: PropTypes.object,
  recaptcha: PropTypes.shape({
    token: PropTypes.string,
    version: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired,
  next: PropTypes.string
}

export default connect(
  loginBoxSelector,
  null,
  (stateProps, dispatchProps, ownProps) => {
    const { dispatch } = dispatchProps
    return {
      ...stateProps,
      ...ownProps,
      onSubmit: (data) =>
        dispatchLogin(
          dispatch,
          { ...data, recaptcha: stateProps.recaptcha },
          ownProps
        )
    }
  }
)(LoginBox)
